// Черновата при отваряне на лист за НОВ документ — точно веднъж:
//  • от началния екран с id (моята от сървъра или поета от колега) → зарежда я;
//  • „Продължи" на локалната / „Изтрий и започни нова" → изборът е направен;
//  • директен адрес (таблото, менюто, отметка) → моята незавършена от СЪРВЪРА,
//    а до BE без черновите / без мрежа / в локален режим — локалната; има ли
//    нещо в нея, листът пита „Да продължите ли…" (`pending`), иначе мълчи.
// Без проверката на сървъра първото натискане в празния лист презаписваше
// моята чернова от друго устройство (autosave-ът е upsert на ЕДНА на тип).
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { deleteDraft, getDraft, listDrafts, type DraftType } from "../../../../api/inventoryDrafts";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import type { DraftRouterState, LocalDraftStore } from "./draftModel";

// Черновата, от която листът може да продължи
export type ResumableDraft<P> = {
  payload: P;
  // сървърната чернова (null — само локалната)
  draftId: number | null;
  own: boolean;
  source: "server" | "local";
};

export type DraftOnOpen<P> =
  | { action: "apply"; draft: ResumableDraft<P> }
  | { action: "ask"; draft: ResumableDraft<P> }
  | { action: "none" };

type FindParams<P> = {
  type: DraftType;
  state: DraftRouterState | null;
  local: LocalDraftStore<P>;
  hasContent: (payload: P) => boolean;
  // локален режим (каса без интернет) — към сървъра не се пита
  serverAllowed: boolean;
};

const isPayload = (value: unknown): boolean => !!value && typeof value === "object";

// Какво намира листът при отваряне. Грешка при заявена чернова (`draftId`) се
// хвърля — листът казва „не се зареди" и се връща на началния екран.
export async function findDraftOnOpen<P>({
  type,
  state,
  local,
  hasContent,
  serverAllowed,
}: FindParams<P>): Promise<DraftOnOpen<P>> {
  if (state?.draftId != null) {
    const detail = await getDraft<P>(state.draftId);
    if (!isPayload(detail.payload)) return { action: "none" };
    return {
      action: "apply",
      draft: { payload: detail.payload, draftId: detail.id, own: state.ownDraft ?? true, source: "server" },
    };
  }
  if (state?.draftChoice === "discard") return { action: "none" };
  const localPayload = local.read();
  const localDraft: ResumableDraft<P> | null =
    localPayload && hasContent(localPayload)
      ? { payload: localPayload, draftId: null, own: true, source: "local" }
      : null;
  if (state?.draftChoice === "continue") return localDraft ? { action: "apply", draft: localDraft } : { action: "none" };
  if (serverAllowed) {
    try {
      const mine = (await listDrafts(type)).find((draft) => draft.mine);
      // колега държи моята чернова отворена — неговата редакция не се пипа
      if (mine?.lockActive) return { action: "none" };
      if (mine) {
        const detail = await getDraft<P>(mine.id);
        if (isPayload(detail.payload) && hasContent(detail.payload))
          return { action: "ask", draft: { payload: detail.payload, draftId: mine.id, own: true, source: "server" } };
      }
    } catch {
      // 404 (BE без черновите), мрежа — остава локалната
    }
  }
  return localDraft ? { action: "ask", draft: localDraft } : { action: "none" };
}

type Params<P> = Omit<FindParams<P>, "state" | "serverAllowed"> & {
  // само нов документ (и без друг източник — prefill, фактура над стокови)
  enabled: boolean;
  routerState: DraftRouterState | null;
  // зареди черновата в листа (без `touch` — не е „незапазена промяна")
  onApply: (draft: ResumableDraft<P>) => void;
  // търсенето приключи (каквото и да намери) — страница, която чака черновата
  // (изписването), показва листа чак тогава
  onSettled?: () => void;
  onLoadError: () => void;
};

export const useDraftResume = <P>({ enabled, routerState, onApply, onSettled, onLoadError, ...find }: Params<P>) => {
  const queryClient = useQueryClient();
  const serverAllowed = !useIsEdgeLocal();
  const [pending, setPending] = useState<ResumableDraft<P> | null>(null);
  const loadedRef = useRef(false);
  // StrictMode демонтира и монтира наново — отговорът се прилага, докато листът е на екрана
  const mountedRef = useRef(true);
  const paramsRef = useRef({ enabled, routerState, onApply, onSettled, onLoadError, serverAllowed, ...find });
  paramsRef.current = { enabled, routerState, onApply, onSettled, onLoadError, serverAllowed, ...find };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    const params = paramsRef.current;
    if (!params.enabled) return;
    findDraftOnOpen({ ...params, state: params.routerState })
      .then((found) => {
        if (!mountedRef.current) return;
        // отговорът се прилага с актуалните действия (справочниците може вече да са заредени)
        if (found.action === "apply") paramsRef.current.onApply(found.draft);
        else if (found.action === "ask") setPending(found.draft);
        paramsRef.current.onSettled?.();
      })
      .catch(() => {
        if (!mountedRef.current) return;
        paramsRef.current.onSettled?.();
        paramsRef.current.onLoadError();
      });
  }, []);

  const continueDraft = useCallback(() => {
    if (!pending) return;
    setPending(null);
    paramsRef.current.onApply(pending);
  }, [pending]);

  // „Изтрий и започни наново" — черновата изчезва и от браузъра, и от сървъра
  const discardDraft = useCallback(() => {
    if (!pending) return;
    setPending(null);
    paramsRef.current.local.clear();
    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: ["inventory", "drafts"] });
      queryClient.invalidateQueries({ queryKey: ["inventory", "nav-counts"] });
    };
    if (pending.source === "server" && pending.draftId != null)
      void deleteDraft(pending.draftId).catch(() => {}).finally(refresh);
  }, [pending, queryClient]);

  // Esc / клик по фона — затваря въпроса, черновата не се пипа
  const dismiss = useCallback(() => setPending(null), []);

  return { pending, continueDraft, discardDraft, dismiss };
};
